import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { PAGAMENTO_FATURA } from '../../utils/Utils';
import { deISO } from '../movimentacoes/transacaoForm';

// Shapes espelhando o contrato de /importacoes/analisar (backend).
export type LancamentoImportado = {
  data: string; // AAAA-MM-DD
  descricao: string;
  valor: number; // sempre positivo
  tipo: 'R' | 'D';
  parcela?: string | null; // "3/12"
  ehPagamentoDeFatura: boolean;
  categoriaSugeridaId?: number | null;
};

export type ImportacaoAnalisada = {
  tipoDocumento: 'fatura_cartao' | 'extrato_conta';
  banco?: string | null;
  periodoInicio?: string | null;
  periodoFim?: string | null;
  totalDeclarado?: number | null;
  somaLancamentos: number;
  checksumConfere?: boolean | null;
  avisos: string[];
  lancamentos: LancamentoImportado[];
};

export type LinhaRevisao = {
  chave: string;
  lancamento: LancamentoImportado;
  selecionada: boolean;
  duplicata: boolean;
  /** Em fatura, o "pagamento recebido" não vira transação nunca — linha travada. */
  travada: boolean;
  categoriaId: string; // '' = sem categoria
};

/** "03/10", "3 / 10" → "3/10" (compara parcela sem depender de zero à esquerda). */
const normalizarParcela = (parcela?: string | null): string | undefined => {
  const m = parcela?.match(/(\d{1,2})\s*\/\s*(\d{1,2})/);
  return m ? `${Number(m[1])}/${Number(m[2])}` : undefined;
};

/** Extrai a parcela do sufixo "(3/10)" que a própria importação adiciona à descrição. */
const parcelaDaDescricao = (descricao?: string): string | undefined =>
  normalizarParcela(descricao?.match(/\((\d{1,2}\s*\/\s*\d{1,2})\)\s*$/)?.[1]);

/**
 * Duplicata provável: já existe transação no app com o MESMO dia, MESMO valor e
 * MESMO tipo. Descrição fica de fora de propósito (o banco abrevia diferente do
 * que o usuário digitou). Falso positivo é barato: a linha vem só desmarcada.
 *
 * Exceção — PARCELAS: a fatura imprime toda parcela com a data da COMPRA
 * original e o mesmo valor, então a parcela 4/10 de fevereiro é idêntica em
 * dia+valor à 3/10 importada em janeiro. Quando o lançamento novo tem parcela,
 * só é duplicata se a transação existente for a MESMA parcela (sufixo "(3/10)"
 * igual na descrição). Sufixo diferente ou ausente = parcela nova, importa.
 */
export const ehProvavelDuplicata = (l: LancamentoImportado, existentes: Transacao[]): boolean => {
  const dia = deISO(l.data);
  const parcelaNova = normalizarParcela(l.parcela);
  return existentes.some((t) => {
    if (t.tipo !== l.tipo) return false;
    if (Math.abs(Number(t.valor) - l.valor) > 0.005) return false;
    const dataT = t.data instanceof Date ? t.data : deISO(String(t.data).slice(0, 10));
    if (
      dataT.getFullYear() !== dia.getFullYear() ||
      dataT.getMonth() !== dia.getMonth() ||
      dataT.getDate() !== dia.getDate()
    )
      return false;
    if (parcelaNova) return parcelaDaDescricao(t.descricao) === parcelaNova;
    return true;
  });
};

/**
 * Prepara as linhas da revisão com defaults seguros:
 * - duplicata provável → desmarcada (o usuário decide);
 * - fatura: "pagamento recebido" → travada e desmarcada (quitação não é lançamento;
 *   o app já tem o Registrar pagamento e o extrato da conta é a fonte certa);
 * - extrato: pagamento de fatura → marcada (é caixa real saindo da conta; vira
 *   lançamento com o tipoPagamento especial que fica fora das análises).
 */
export const prepararLinhas = (
  analise: ImportacaoAnalisada,
  existentes: Transacao[]
): LinhaRevisao[] =>
  analise.lancamentos.map((lancamento, indice) => {
    const duplicata = ehProvavelDuplicata(lancamento, existentes);
    const travada = analise.tipoDocumento === 'fatura_cartao' && lancamento.ehPagamentoDeFatura;
    return {
      chave: `${indice}`,
      lancamento,
      duplicata,
      travada,
      selecionada: !duplicata && !travada,
      categoriaId: lancamento.categoriaSugeridaId?.toString() ?? ''
    };
  });

/**
 * Converte as linhas SELECIONADAS em payloads do POST /transacoes.
 * - Fatura: tudo vai pro cartão escolhido como "Cartão de Crédito" (compras "D",
 *   estornos "R" — a fatura estimada já trata receita como abatimento). Parcela
 *   "3/12" vira sufixo da descrição: cada fatura importada traz a própria parcela,
 *   então NÃO criamos série (ehParcelada) — senão duplicaria nas próximas.
 * - Extrato: tudo vai pra conta escolhida; pagamento de fatura vira o
 *   tipoPagamento especial, sem categoria (quitação não é despesa).
 */
export const montarTransacoesDaImportacao = (
  linhas: LinhaRevisao[],
  tipoDocumento: ImportacaoAnalisada['tipoDocumento'],
  destinoId: number
): Transacao[] =>
  linhas
    .filter((linha) => linha.selecionada && !linha.travada)
    .map(({ lancamento, categoriaId }) => {
      const descricao = lancamento.parcela
        ? `${lancamento.descricao} (${lancamento.parcela})`
        : lancamento.descricao;

      const base: Transacao = {
        tipo: lancamento.tipo,
        valor: lancamento.valor,
        data: deISO(lancamento.data),
        descricao
      };

      if (tipoDocumento === 'fatura_cartao') {
        return {
          ...base,
          cartaoCreditoId: destinoId,
          tipoPagamento: 'Cartão de Crédito',
          categoriaId: categoriaId ? Number(categoriaId) : undefined
        };
      }

      if (lancamento.ehPagamentoDeFatura) {
        // Quitação: conta como saída da conta, fora de todas as análises.
        return { ...base, contaId: destinoId, tipoPagamento: PAGAMENTO_FATURA };
      }

      return {
        ...base,
        contaId: destinoId,
        categoriaId: categoriaId ? Number(categoriaId) : undefined
      };
    });

/** Total (R − D) das linhas selecionadas, pro rodapé da revisão. */
export const totalSelecionado = (linhas: LinhaRevisao[]): { quantidade: number; total: number } => {
  const ativas = linhas.filter((linha) => linha.selecionada && !linha.travada);
  return {
    quantidade: ativas.length,
    total: ativas.reduce(
      (soma, { lancamento }) => soma + (lancamento.tipo === 'R' ? lancamento.valor : -lancamento.valor),
      0
    )
  };
};
